import { useCallback, useMemo, useRef } from 'react';
import { GlobalBannerDynamic } from '@frontend/global-info-banner';
import { useTranslation } from '@frontend/i18n';
import { useEligibility } from '@frontend/user-preferences';
import { useNavigate } from '@frontend/weave-router';
import { CALL_INTEL_BASE_URL } from '../../constants';
import { backfillBannerPendoEvents, backfillBannerTrackingIds, trackBackfillBannerEvent } from '../../tracking-ids';

interface MixedGlobalBannerProps {
  batchType: 'batch1' | 'batch2';
  locationCount: number;
  onCallIntelligenceRoute: boolean;
}

export const MixedGlobalBanner = ({ batchType, locationCount, onCallIntelligenceRoute }: MixedGlobalBannerProps) => {
  const { t } = useTranslation('call-intelligence');
  const navigate = useNavigate();

  const hasFiredPendoRef = useRef(false);

  const { shouldShow, recordDismiss } = useEligibility({
    featureId: 'call-intel-backfill-mixed-banner',
    defaultCriteria: {
      type: 'dismiss-cooldown',
      maxDismisses: 99999,
      cooldownDays: 7,
    },
  });

  const handleDismiss = useCallback(() => {
    recordDismiss();
  }, [recordDismiss]);

  const action = useMemo(
    () => ({
      label: t('Go to Call Intelligence'),
      onClick: () => {
        recordDismiss();
        navigate({ to: CALL_INTEL_BASE_URL });
      },
    }),
    [navigate, recordDismiss, t]
  );

  const title = useMemo(() => {
    if (batchType === 'batch1') return t('Start Exploring Your Call Intelligence Subscription!');
    return t('Your Call Intelligence Data is Ready!');
  }, [batchType, t]);

  const message = useMemo(() => {
    if (batchType === 'batch1') {
      return t("We've finished analyzing your first set of calls to give you quick access to your insights.");
    }
    return t(
      'Your call insights are ready for {{count}} locations. Dive into patient needs, outcomes, and next steps to make every conversation count.',
      { count: locationCount }
    );
  }, [batchType, locationCount, t]);

  const handleVisible = useCallback(() => {
    if (!hasFiredPendoRef.current) {
      hasFiredPendoRef.current = true;
      trackBackfillBannerEvent(backfillBannerPendoEvents.mixedBannerSeen, { batchType });
    }
  }, [batchType]);

  if (!shouldShow || onCallIntelligenceRoute) return null;

  return (
    <GlobalBannerDynamic
      type='premium'
      surfaceId='banner.call-intel-backfill'
      id='call-intel-backfill-mixed-ready'
      title={title}
      message={message}
      isDismissible
      onDismiss={handleDismiss}
      onVisible={handleVisible}
      action={action}
      trackingId={backfillBannerTrackingIds.mixedBannerCta}
      dismissBtnTrackingId={backfillBannerTrackingIds.mixedBannerDismiss}
    />
  );
};
